import { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if this is a staff login
    const staffToken = localStorage.getItem('staff_token');
    if (staffToken) {
      try {
        const staffSession = JSON.parse(staffToken);
        setUser({
          id: staffSession.staff.id,
          name: staffSession.staff.name,
          email: staffSession.staff.email,
          role: 'staff',
          restaurant_id: staffSession.restaurant_id,
          permissions: staffSession.permissions,
          isStaffLogin: true
        });
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing staff token:', error);
      }
    }
    
    if (token && token !== 'staff_authenticated') {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

const login = async (email, password) => {
  const response = await axios.post(`${API}/auth/login`, { email, password });

  const { access_token, user: userData } = response.data;

  localStorage.setItem('token', access_token);
  setToken(access_token);

  localStorage.setItem('user', JSON.stringify(userData));
  setUser(userData);

  if (userData?.restaurant_id) {
    localStorage.setItem(
      'current_restaurant_id',
      userData.restaurant_id
    );
  }

  setLoading(false);
  return userData;
};


  const register = async (userData) => {
    await axios.post(`${API}/auth/register`, userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('restaurant');
    localStorage.removeItem('staff_token'); // Clear staff token too
    setToken(null);
    setUser(null);
  };

  const impersonate = (impersonateToken, impersonateUser) => {
    // Clear admin tokens
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    // Set restaurant tokens
    localStorage.setItem('token', impersonateToken);
    localStorage.setItem('user', JSON.stringify(impersonateUser));
    
    // Update state immediately
    setToken(impersonateToken);
    setUser(impersonateUser);
    setLoading(false);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    impersonate,  // Add impersonate method
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
